import { instalarErrorBoundary } from '@plataforma/utils';

instalarErrorBoundary({ prefijo: 'sitio-publico', mostrarBanner: false });

import '../styles/main.css';

import { contarItemsCarrito, onCambioCarrito } from './carrito.js';
import { botonWhatsAppFlotanteHtml, escaparHtml } from './componentes.js';
import { cargarTiendaPublicada } from './datos-publicados.js';
import { iconoLucide } from './iconos.js';
import {
  avisoEstadoTiendaHtml,
  conectarBuscadorExpandible,
  conectarDrawersMoviles,
  drawerCarritoHtml,
  drawerMenuHtml,
  footerHtml,
  headerHtml,
  miniCarritoHtml,
} from './layout.js';
import { aplicarEstadoVisual } from './motortema.js';
import { iniciarRouter, navegarA, partirRuta } from './router.js';
import {
  aplicarMetadatos,
  calcularMetaCarrito,
  calcularMetaCatalogo,
  calcularMetaContacto,
  calcularMetaHome,
  calcularMetaNoEncontrado,
  calcularMetaProducto,
} from './seo.js';
import { aplicarFavicon, aplicarTemaVisual } from './tema.js';
import { vistaCarrito } from './vistas/carrito.js';
import { vistaCatalogo } from './vistas/catalogo.js';
import { vistaContacto } from './vistas/contacto.js';
import { vistaHome } from './vistas/home.js';
import { vistaProducto } from './vistas/producto.js';

const app = document.querySelector('#app');

function pantallaCarga() {
  app.innerHTML = `
    <main class="sp-pantalla-estado">
      <p class="sp-eyebrow">Cargando tienda</p>
      <h1>Preparando catálogo...</h1>
      <div class="sp-skeleton sp-skeleton--carga" aria-hidden="true"></div>
    </main>
  `;
}

function pantallaError(error) {
  app.innerHTML = `
    <main class="sp-pantalla-estado">
      <p class="sp-eyebrow">Tienda no disponible</p>
      <h1>No pudimos cargar la tienda publicada</h1>
      <p>${escaparHtml(error.message)}</p>
    </main>
  `;
}

function montarLayout(tienda, estado) {
  app.innerHTML = `
    ${avisoEstadoTiendaHtml(tienda, estado)}
    ${headerHtml(tienda, estado)}
    <div class="sp-overlay" data-sp-overlay hidden></div>
    ${drawerMenuHtml(tienda, estado)}
    ${drawerCarritoHtml(tienda)}
    <main id="sp-vista"></main>
    ${footerHtml(tienda, estado)}
    ${botonWhatsAppFlotanteHtml(tienda)}
  `;
}

function actualizarContadorCarrito() {
  const contador = document.querySelector('#sp-contador-carrito');
  if (!contador) return;

  contador.textContent = contarItemsCarrito();
  contador.classList.remove('sp-contador--animado');
  requestAnimationFrame(() => contador.classList.add('sp-contador--animado'));
}

function conectarLayout(tienda, estado) {
  const { cerrar: cerrarDrawers } = conectarDrawersMoviles();
  const header = document.querySelector('[data-sp-header]');
  let scrollAnterior = window.scrollY;

  document.querySelectorAll('[data-sp-busqueda]').forEach((formulario) => {
    formulario.addEventListener('submit', (evento) => {
      evento.preventDefault();
      const input = formulario.querySelector('input[name="q"]');
      const termino = input?.value.trim();
      cerrarDrawers();
      navegarA(termino ? `/catalogo?q=${encodeURIComponent(termino)}` : '/catalogo');
    });
  });

  conectarBuscadorExpandible();

  // El header se oculta al hacer scroll hacia abajo solo en las variantes con
  // fondo solido (sobre contenido blanco); las transparentes se pegan arriba.
  if (estado.header.caracteristicas.fondo_solido) {
    window.addEventListener(
      'scroll',
      () => {
        const actual = window.scrollY;
        header?.classList.toggle('sp-header--oculto', actual > scrollAnterior && actual > 96);
        scrollAnterior = actual;
      },
      { passive: true },
    );
  }

  onCambioCarrito(() => {
    actualizarContadorCarrito();
    const mini = document.querySelector('[data-sp-mini-carrito]');
    if (mini) mini.innerHTML = miniCarritoHtml(tienda);
  });
}

function calcularMetaRuta(tienda, pathname, params, origen) {
  if (pathname === '/') return calcularMetaHome(tienda, origen);
  if (pathname === '/catalogo') {
    const categoriaSlug = params.get('categoria') ?? '';
    const categoriaActual = tienda.categorias.find((categoria) => categoria.slug === categoriaSlug);
    return calcularMetaCatalogo(tienda, params, categoriaActual, origen);
  }
  if (pathname === '/carrito') return calcularMetaCarrito(tienda, origen);
  if (pathname === '/contacto') return calcularMetaContacto(tienda, origen);
  if (pathname.startsWith('/producto/')) {
    const slugProducto = decodeURIComponent(pathname.replace('/producto/', ''));
    const producto = tienda.productos.find((item) => item.slug === slugProducto);
    if (producto) return calcularMetaProducto(tienda, producto, origen);
  }
  return calcularMetaNoEncontrado(origen);
}

function renderizarRuta(tienda, estado, ruta) {
  const vista = document.querySelector('#sp-vista');
  const { pathname, params } = partirRuta(ruta);
  vista.innerHTML = '';
  window.scrollTo({ top: 0, behavior: 'auto' });

  aplicarMetadatos(calcularMetaRuta(tienda, pathname, params, window.location.origin));

  if (pathname === '/') vistaHome(vista, tienda, estado);
  else if (pathname === '/catalogo') vistaCatalogo(vista, tienda, params, estado);
  else if (pathname === '/carrito') vistaCarrito(vista, tienda);
  else if (pathname === '/contacto') vistaContacto(vista, tienda);
  else if (pathname.startsWith('/producto/'))
    vistaProducto(vista, tienda, decodeURIComponent(pathname.replace('/producto/', '')));
  else {
    vista.innerHTML = `
      <section class="sp-page sp-contenedor sp-vacio">
        <span class="sp-vacio__icono" aria-hidden="true">${iconoLucide('Package')}</span>
        <h1>Página no encontrada</h1>
        <a class="sp-boton sp-boton--secundario" href="/">Volver al inicio</a>
      </section>
    `;
  }

  actualizarContadorCarrito();
}

async function iniciar() {
  pantallaCarga();

  try {
    const tienda = await cargarTiendaPublicada();
    aplicarTemaVisual(tienda.tema);
    // Zona superior EXTERNA del navegador (barra de hora/señal/WiFi/batería):
    // se deja neutra/blanca fija, independiente del tema y del header del DOM.
    // iOS 26+ (Safari) ignora `meta[name="theme-color"]` y deriva el color de
    // esa zona a partir del background del <html> (root). Fijarlo a blanco
    // mantiene la barra externa blanca para todas las tiendas sin tocar
    // `color_fondo` (body), el header real, la lógica de contraste ni la
    // paleta. Android sigue leyendo el theme-color (blanco) y no se ve
    // afectado por este fondo (queda detrás del <body>).
    document.documentElement.style.backgroundColor = '#ffffff';
    const estado = aplicarEstadoVisual(tienda);
    aplicarFavicon(tienda.perfil.favicon_url);
    montarLayout(tienda, estado);
    conectarLayout(tienda, estado);
    actualizarContadorCarrito();
    iniciarRouter((ruta) => renderizarRuta(tienda, estado, ruta));
  } catch (error) {
    pantallaError(error);
  }
}

iniciar();
